import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { RefreshCw, X } from 'lucide-react';
import './PreInscriptionModal.css';

const DEFAULT_TAG = 'clevory academy';

const studyLevels = [
	{ value: '', label: "Niveau d'études" },
	{ value: 'bac', label: 'Bac' },
	{ value: 'bac+2', label: 'Bac+2' },
	{ value: 'bac+3', label: 'Bac+3' },
	{ value: 'bac+5', label: 'Bac+5' },
	{ value: 'autre', label: 'Autre' },
];

const defaultTracks = [
	{ value: '', label: 'Choisir un parcours' },
	{ value: 'IT SALES', label: 'IT Sales – Business Developer IT' },
	{ value: 'AGENTIC AI', label: 'Agentic IA – Concepteur d’Agents IA' },
	{ value: 'FORMATION À LA CARTE', label: 'Formation à la carte' },
	{ value: 'A ORIENTER', label: 'Je souhaite être orienté' },
];

const routeTrackConfig = {
	'/formations/it-sales/pre-inscription': {
		mode: 'preinscription',
		title: 'PRÉ-INSCRIPTION IT SALES',
		subtitle: 'GRATUITE',
		submitLabel: "S'inscrire",
		successMessage: '✅ Pré-inscription envoyée avec succès.',
		tracks: [
			{ value: '', label: 'Choisir un parcours' },
			{ value: 'IT SALES NIVEAU 1', label: 'It sales - Niveau 1' },
			{ value: 'IT SALES NIVEAU 2', label: 'It sales - Niveau 2' },
			{ value: 'IT SALES LES 2 NIVEAUX', label: 'It sales - Les 2 niveaux' },
		],
		suggestedTrack: 'IT SALES LES 2 NIVEAUX',
		extraTags: [],
	},
	'/formations/agentic-ia/pre-inscription': {
		mode: 'preinscription',
		title: 'PRÉ-INSCRIPTION AGENTIC AI',
		subtitle: 'GRATUITE',
		submitLabel: "S'inscrire",
		successMessage: '✅ Pré-inscription envoyée avec succès.',
		tracks: [
			{ value: '', label: 'Choisir un parcours' },
			{ value: 'AGENTIC AI NIVEAU 1', label: 'Agentic AI - Niveau 1' },
			{ value: 'AGENTIC AI NIVEAU 2', label: 'Agentic AI - Niveau 2' },
			{
				value: 'AGENTIC AI LES 2 NIVEAUX',
				label: 'Agentic AI - Les 2 niveaux',
			},
		],
		suggestedTrack: 'AGENTIC AI LES 2 NIVEAUX',
		extraTags: [],
	},
};

const defaultModalConfig = {
	mode: 'preinscription',
	title: 'PRÉ-INSCRIPTION',
	subtitle: 'GRATUITE',
	submitLabel: "S'inscrire",
	successMessage: '✅ Pré-inscription envoyée avec succès.',
	tracks: defaultTracks,
	suggestedTrack: '',
	extraTags: [],
};

const initialForm = {
	fullName: '',
	email: '',
	phone: '',
	level: '',
	track: '',
	captchaInput: '',
};

function generateCaptcha() {
	const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
	let result = '';
	for (let i = 0; i < 4; i += 1) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return result;
}

function getRouteConfig(pathname) {
	const matchedRoute = Object.keys(routeTrackConfig)
		.sort((a, b) => b.length - a.length)
		.find((route) => pathname.startsWith(route));

	return matchedRoute ? routeTrackConfig[matchedRoute] : null;
}

function getFallbackReturnPath(pathname, stateReturnPath) {
	if (stateReturnPath) return stateReturnPath;

	if (pathname.startsWith('/formations/it-sales/pre-inscription')) {
		return '/formations/it-sales';
	}

	if (pathname.startsWith('/formations/agentic-ia/pre-inscription')) {
		return '/formations/agentic-ia';
	}

	return '/';
}

function normalizeTags(tags) {
	if (!Array.isArray(tags)) return [];
	return tags.map((tag) => String(tag || '').trim()).filter(Boolean);
}

function mergeTagsWithDefault(extraTags = []) {
	const merged = [DEFAULT_TAG, ...normalizeTags(extraTags)];
	return [...new Set(merged)];
}

async function postJson(url, payload, fallbackErrorMessage) {
	const response = await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(payload),
	});

	let data = {};
	try {
		data = await response.json();
	} catch {
		data = {};
	}

	if (!response.ok || data.success === false || data.ok === false) {
		throw new Error(
			data.error ||
				data.message ||
				fallbackErrorMessage ||
				"Erreur lors de l'envoi.",
		);
	}

	return data;
}

export default function PreInscriptionModal({ onClose = null, config = null }) {
	const location = useLocation();
	const navigate = useNavigate();

	const [form, setForm] = useState(initialForm);
	const [errors, setErrors] = useState({});
	const [captcha, setCaptcha] = useState(generateCaptcha());
	const [isVisible, setIsVisible] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitMessage, setSubmitMessage] = useState({
		type: '',
		text: '',
	});

	const resolvedConfig = useMemo(() => {
		if (config) {
			const tracks = config.tracks?.length ? config.tracks : defaultTracks;

			return {
				...defaultModalConfig,
				...config,
				tracks,
				extraTags: normalizeTags(config.extraTags),
				suggestedTrack:
					config.suggestedTrack || (tracks.length > 1 ? tracks[1].value : ''),
			};
		}

		const routeConfig = getRouteConfig(location.pathname) || defaultModalConfig;

		return {
			...routeConfig,
			extraTags: normalizeTags(routeConfig.extraTags),
		};
	}, [config, location.pathname]);

	const modalClasses = useMemo(
		() =>
			`pre-inscription-modal ${isVisible ? 'pre-inscription-modal--open' : ''}`,
		[isVisible],
	);

	useEffect(() => {
		const id = requestAnimationFrame(() => setIsVisible(true));
		return () => cancelAnimationFrame(id);
	}, []);

	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, []);

	useEffect(() => {
		setForm((prev) => {
			const currentTrackExists = resolvedConfig.tracks.some(
				(item) => item.value === prev.track,
			);

			return {
				...prev,
				track: currentTrackExists ? prev.track : resolvedConfig.suggestedTrack,
			};
		});
		setErrors((prev) => ({ ...prev, track: '' }));
	}, [resolvedConfig]);

	useEffect(() => {
		if (!submitMessage.text) return;

		const timer = setTimeout(() => {
			setSubmitMessage({ type: '', text: '' });
		}, 3500);

		return () => clearTimeout(timer);
	}, [submitMessage]);

	const handleChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		setErrors((prev) => ({ ...prev, [field]: '' }));
	};

	const performCloseAction = () => {
		if (typeof onClose === 'function') {
			onClose();
			return;
		}

		const fallbackPath = getFallbackReturnPath(
			location.pathname,
			location.state?.returnPath,
		);

		if (location.state?.backgroundLocation) {
			navigate(-1);
			return;
		}

		if (window.history.length > 1) {
			navigate(-1);
			return;
		}

		navigate(fallbackPath, { replace: true });
	};

	const handleClose = () => {
		setIsVisible(false);
		setTimeout(() => {
			performCloseAction();
		}, 240);
	};

	const refreshCaptcha = () => {
		const next = generateCaptcha();
		setCaptcha(next);
		setForm((prev) => ({ ...prev, captchaInput: '' }));
		setErrors((prev) => ({ ...prev, captchaInput: '' }));
	};

	const resetForm = () => {
		setForm({
			...initialForm,
			track: resolvedConfig.suggestedTrack || '',
		});
		setErrors({});
		refreshCaptcha();
	};

	const validate = () => {
		const nextErrors = {};

		if (!form.fullName.trim()) {
			nextErrors.fullName = 'Le nom et prénom sont obligatoires.';
		}

		if (!form.email.trim()) {
			nextErrors.email = "L'adresse mail est obligatoire.";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
			nextErrors.email = 'Adresse mail invalide.';
		}

		if (!form.phone) {
			nextErrors.phone = 'Le téléphone est obligatoire.';
		} else if (!isValidPhoneNumber(form.phone)) {
			nextErrors.phone = 'Numéro de téléphone invalide.';
		}

		if (!form.level) {
			nextErrors.level = 'Choisissez un niveau.';
		}

		if (!form.track) {
			nextErrors.track = 'Choisissez un parcours.';
		}

		if (!form.captchaInput.trim()) {
			nextErrors.captchaInput = 'Veuillez saisir le captcha.';
		} else if (form.captchaInput.trim().toUpperCase() !== captcha) {
			nextErrors.captchaInput = 'Captcha incorrect.';
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const buildOdooPayload = () => {
		const finalTags = mergeTagsWithDefault(resolvedConfig.extraTags);

		return {
			params: {
				name: form.fullName.trim(),
				email: form.email.trim(),
				phone: form.phone.trim(),
				parcours: form.track,
				niveau_scolaire: form.level,
				form_type: resolvedConfig.mode,
				tag_ids: finalTags.length ? finalTags : [DEFAULT_TAG],
			},
		};
	};

	const buildNotificationPayload = () => {
		return {
			name: form.fullName.trim(),
			email: form.email.trim(),
			phone: form.phone.trim(),
			niveau_scolaire: form.level,
			program: form.track,
		};
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validate()) return;

		setIsSubmitting(true);
		setSubmitMessage({ type: '', text: '' });

		const odooApiUrl =
			process.env.REACT_APP_API_URL ||
			'https://portal.clevory.info/testwordpress';

		const notificationApiUrl =
			process.env.REACT_APP_NOTIFICATION_API_URL ||
			'https://notifications.ted-university.com/api/contact/clevory-academy';

		const isBrochureMode = resolvedConfig.mode === 'brochure';

		const odooPromise = postJson(
			odooApiUrl,
			buildOdooPayload(),
			"Erreur lors de l'envoi vers l'ERP.",
		);

		const notificationPromise = isBrochureMode
			? postJson(
					notificationApiUrl,
					buildNotificationPayload(),
					"Erreur lors de l'envoi de l'email de notification.",
				)
			: Promise.resolve({ skipped: true });

		try {
			const [odooResult, notificationResult] = await Promise.allSettled([
				odooPromise,
				notificationPromise,
			]);

			if (odooResult.status === 'rejected') {
				throw new Error(
					odooResult.reason?.message || "Erreur lors de l'envoi vers l'ERP.",
				);
			}

			if (isBrochureMode && notificationResult.status === 'rejected') {
				throw new Error(
					notificationResult.reason?.message ||
						"Erreur lors de l'envoi de l'email de notification.",
				);
			}

			setSubmitMessage({
				type: 'success',
				text: resolvedConfig.successMessage,
			});

			resetForm();

			setTimeout(() => {
				handleClose();
			}, 1200);
		} catch (error) {
			console.error('Erreur API Odoo/Notification:', error);
			setSubmitMessage({
				type: 'error',
				text:
					error.message ||
					'❌ Une erreur est survenue. Veuillez réessayer plus tard.',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={modalClasses} aria-modal='true' role='dialog'>
			<button
				type='button'
				className='pre-inscription-modal__backdrop'
				onClick={handleClose}
				aria-label='Fermer la fenêtre'
			/>

			<div className='pre-inscription-modal__dialog'>
				<button
					type='button'
					className='pre-inscription-modal__close'
					onClick={handleClose}
					aria-label='Fermer'>
					<X size={18} />
				</button>

				<div className='pre-inscription-modal__card'>
					<h2 className='pre-inscription-modal__title'>
						{resolvedConfig.title}
					</h2>

					{resolvedConfig.subtitle ? (
						<p className='pre-inscription-modal__subtitle'>
							{resolvedConfig.subtitle}
						</p>
					) : null}

					<form
						className='pre-inscription-form'
						onSubmit={handleSubmit}
						noValidate>
						<div className='pre-inscription-form__group'>
							<input
								type='text'
								placeholder='Nom et Prénom'
								value={form.fullName}
								onChange={(e) => handleChange('fullName', e.target.value)}
								className={`pre-inscription-form__input ${
									errors.fullName ? 'is-error' : ''
								}`}
							/>
							{errors.fullName && (
								<p className='pre-inscription-form__error'>{errors.fullName}</p>
							)}
						</div>

						<div className='pre-inscription-form__group'>
							<input
								type='email'
								placeholder='Adresse mail'
								value={form.email}
								onChange={(e) => handleChange('email', e.target.value)}
								className={`pre-inscription-form__input ${
									errors.email ? 'is-error' : ''
								}`}
							/>
							{errors.email && (
								<p className='pre-inscription-form__error'>{errors.email}</p>
							)}
						</div>

						<div className='pre-inscription-form__group'>
							<div
								className={`pre-inscription-form__phone ${
									errors.phone ? 'is-error' : ''
								}`}>
								<PhoneInput
									international
									defaultCountry='TN'
									countryCallingCodeEditable={false}
									placeholder='Numéro de Téléphone'
									value={form.phone}
									onChange={(value) => handleChange('phone', value || '')}
									className='pre-inscription-form__phone-input'
								/>
							</div>
							{errors.phone && (
								<p className='pre-inscription-form__error'>{errors.phone}</p>
							)}
						</div>

						<div className='pre-inscription-form__group'>
							<select
								value={form.level}
								onChange={(e) => handleChange('level', e.target.value)}
								className={`pre-inscription-form__input ${
									errors.level ? 'is-error' : ''
								}`}>
								{studyLevels.map((item) => (
									<option key={item.value || item.label} value={item.value}>
										{item.label}
									</option>
								))}
							</select>
							{errors.level && (
								<p className='pre-inscription-form__error'>{errors.level}</p>
							)}
						</div>

						<div className='pre-inscription-form__group'>
							<select
								value={form.track}
								onChange={(e) => handleChange('track', e.target.value)}
								className={`pre-inscription-form__input ${
									errors.track ? 'is-error' : ''
								}`}>
								{resolvedConfig.tracks.map((item) => (
									<option key={item.value || item.label} value={item.value}>
										{item.label}
									</option>
								))}
							</select>
							{errors.track && (
								<p className='pre-inscription-form__error'>{errors.track}</p>
							)}
						</div>

						<div className='pre-inscription-form__group'>
							<div className='pre-inscription-form__captcha-row'>
								<input
									type='text'
									placeholder='Entrez le captcha'
									value={form.captchaInput}
									onChange={(e) =>
										handleChange('captchaInput', e.target.value.toUpperCase())
									}
									className={`pre-inscription-form__input pre-inscription-form__captcha-input ${
										errors.captchaInput ? 'is-error' : ''
									}`}
									autoComplete='off'
								/>

								<div
									className='pre-inscription-form__captcha-box'
									aria-label='Code captcha'>
									{captcha}
								</div>

								<button
									type='button'
									className='pre-inscription-form__captcha-refresh'
									onClick={refreshCaptcha}
									aria-label='Rafraîchir le captcha'>
									<RefreshCw size={16} />
								</button>
							</div>
							{errors.captchaInput && (
								<p className='pre-inscription-form__error'>
									{errors.captchaInput}
								</p>
							)}
						</div>

						{submitMessage.text && (
							<div
								className={`pre-inscription-form__alert pre-inscription-form__alert--${submitMessage.type}`}>
								{submitMessage.text}
							</div>
						)}

						<button
							type='submit'
							disabled={isSubmitting}
							className='pre-inscription-form__submit'>
							{isSubmitting ? 'Envoi en cours...' : resolvedConfig.submitLabel}
						</button>
					</form>
				</div>
			</div>
		</div>
	);
}
